import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useVoted } from "@/lib/cards";
import { MysteryCard } from "@/components/MysteryCard";

const PAGE = 12;



function Filters({ q, cat, esito, set, cats }) {
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_200px_200px]">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
        <Input data-testid="search-input" aria-label="Cerca nei misteri" value={q} onChange={(e) => set("q", e.target.value)} placeholder="Titolo, personaggio, parola chiave…" className="h-11 rounded-xl bg-white pl-9" />
      </div>
      <select data-testid="filter-category-select" aria-label="Categoria" className="luma-select h-11 bg-white" value={cat} onChange={(e) => set("categoria", e.target.value)}>
        <option value="">Tutte le categorie</option>
        {cats.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <select data-testid="filter-state-select" aria-label="Stato del mistero" className="luma-select h-11 bg-white" value={esito} onChange={(e) => set("esito", e.target.value)}>
        <option value="">Tutti i misteri</option>
        <option value="aperto">Ancora irrisolti</option>
        <option value="chiuso">Scelte chiuse</option>
        <option value="risolto">Rivelati nel manga</option>
      </select>
    </div>
  );
}

export default function Misteri() {
  const { user } = useAuth();
  const voted = useVoted();
  const [params, setParams] = useSearchParams();
  const loc = useLocation();
  const [q, setQ] = useState(() => loc.state?.q || params.get("q") || "");
  const cat = params.get("categoria") || "";
  const esito = params.get("esito") || "";
  const [bozze, setBozze] = useState(false);
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState(null);
  const [shown, setShown] = useState(PAGE);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);

  const set = (k, v) => {
    if (k === "q") { setQ(v); return; }
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  };

  useEffect(() => {
    if (!params.has("q")) return;
    const p = new URLSearchParams(params);
    p.delete("q");
    setParams(p, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { api.get("/meta").then((r) => setCats(r.data.categorie)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/dossiers", { params: { q, categoria: cat, esito, bozze } })
        .then((r) => { setItems(r.data); setShown(PAGE); setError(""); })
        .catch((e) => setError(e.response?.data?.detail ? errMsg(e) : "Impossibile caricare i dossier in questo momento."));
    }, 250);
    return () => clearTimeout(t);
  }, [q, cat, esito, bozze, reload]);

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[var(--luma-pink)]">Le teorie dal mondo</p>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h1 className="text-4xl font-black sm:text-5xl">Tutti i misteri</h1>
          <Link to="/classifica" data-testid="archive-leaderboard-link" className="text-sm font-bold text-[var(--luma-purple)] hover:underline">Classifica →</Link>
        </div>
        {items && <p data-testid="archive-count" className="text-sm text-muted-foreground">{items.length} {items.length === 1 ? "mistero da esplorare" : "misteri da esplorare"}</p>}
      </div>
      <Filters {...{ q, cat, esito, set, cats }} />
      {user?.role === "admin" && (
        <label className="flex w-fit items-center gap-3 rounded-full border border-violet-500/40 bg-violet-500/10 px-4 py-2 text-sm font-semibold">
          <Switch data-testid="admin-drafts-toggle" checked={bozze} onCheckedChange={setBozze} /> Mostra anche bozze e archiviati (solo Fondatore)
        </label>
      )}
      {error && (
        <div data-testid="archive-error" className="luma-card flex flex-wrap items-center justify-between gap-3 text-sm">
          <span>{error}</span>
          <button onClick={() => setReload(reload + 1)} data-testid="archive-retry-button" className="rounded-full border border-border px-4 py-2 font-semibold hover:border-primary">Riprova</button>
        </div>
      )}
      {!error && items && items.length === 0 && (
        <p data-testid="archive-empty-state" className="luma-card text-center text-muted-foreground">Nessun mistero corrisponde alla ricerca o ai filtri selezionati.</p>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="mystery-grid">
        {(items || []).slice(0, shown).map((d) => <MysteryCard key={d.id} d={d} voted={voted.has(d.id)} />)}
      </div>
      {items && items.length > shown && (
        <div className="text-center">
          <button onClick={() => setShown(shown + PAGE)} data-testid="archive-load-more" className="rounded-full border border-border px-6 py-3 text-sm font-semibold hover:border-primary hover:text-primary">
            Carica altri ({items.length - shown})
          </button>
        </div>
      )}
    </div>
  );
}
